import { ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

import { AccountSidebar } from './AccountSidebar'
import { StatusBanner } from './StatusBanner'

import type { ReactNode } from 'react'

type AccountShellProps = {
    profileName?: string
    profileEmail?: string
    profilePictureUrl?: string | null
    onLogout?: () => void
    signingOut?: boolean
    title: string
    description: string
    status?: {
        tone: 'success' | 'error'
        message: string
    } | null
    children: ReactNode
}

export const AccountShell = ({
    profileName,
    profileEmail,
    profilePictureUrl,
    onLogout,
    signingOut,
    title,
    description,
    status,
    children,
}: AccountShellProps) => {
    const { t } = useTranslation()

    const showSidebar = !!profileName
    const showHeader = !!title || !!description || !!onLogout

    return (
        <div className="bg-account-page text-account-strong min-h-screen font-sans">
            <div className="mx-auto flex max-w-5xl flex-col gap-8 px-4 py-8 md:flex-row md:py-12">
                {showSidebar && (
                    <AccountSidebar name={profileName} email={profileEmail} pictureUrl={profilePictureUrl} />
                )}

                <main className="flex flex-1 flex-col gap-6">
                    {showHeader && (
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                                {title && <h1 className="text-account-strong text-3xl font-bold">{title}</h1>}
                                {description && <p className="text-account-secondary mt-1 text-sm">{description}</p>}
                            </div>

                            {onLogout && (
                                <button
                                    type="button"
                                    onClick={onLogout}
                                    disabled={signingOut}
                                    className="border-account-border bg-account-surface text-account-secondary hover:border-account-error-border hover:text-account-error inline-flex items-center gap-2 self-start rounded-lg border px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
                                    {t('shell.signOut')}
                                </button>
                            )}
                        </div>
                    )}

                    {status && <StatusBanner tone={status.tone} message={status.message} />}

                    {children}
                </main>
            </div>
        </div>
    )
}
